import {
  registerGlobalPlugins,
  type ModuleFederationRuntimePlugin
} from '@module-federation/enhanced/runtime'
import {
  datadogRum,
  type RumEvent,
  type RumEventDomainContext
} from '@datadog/browser-rum'

// Keep in sync with the copy in contrast-ui-react.
type MfeBuild = { remoteName: string; service: string; version: string }

// Keyed by the manifest's directory URL, which is also the base URL of the MFE's chunks.
const mfeBuildsByBaseUrl = new Map<string, MfeBuild>()

const URL_PATTERN = /https?:\/\/[^\s)]+/

// V8 frames start with "at ", Firefox/Safari frames look like "fn@url". Skips the error message line.
const getFirstFrameUrl = (stack: string): string | undefined => {
  for (const line of stack.split('\n')) {
    const frame = line.trim()
    if (frame.startsWith('at ') || frame.includes('@http')) {
      return URL_PATTERN.exec(frame)?.[0]
    }
  }
  return undefined
}

const plugin: ModuleFederationRuntimePlugin = {
  name: 'mfe-version-plugin',
  loadRemoteSnapshot(args) {
    const buildInfo = args.manifestJson?.metaData.buildInfo
    if (args.manifestUrl && buildInfo) {
      const baseUrl = new URL(
        '.',
        new URL(args.manifestUrl, window.location.href)
      ).href
      mfeBuildsByBaseUrl.set(baseUrl, {
        remoteName: args.moduleInfo.name,
        service: buildInfo.buildName,
        version: buildInfo.buildVersion
      })
      datadogRum.setGlobalContextProperty(
        'mfe_versions',
        Object.fromEntries(
          [...mfeBuildsByBaseUrl.values()].map((b) => [b.remoteName, b.version])
        )
      )
    }
    return args
  }
}

// Attributes events to the MFE whose code raised them, so Datadog applies that MFE's source maps.
export const attributeMfeEvent = (
  event: RumEvent,
  context: RumEventDomainContext
): void => {
  // Prefer the error's own stack: errors caught by React are reported from host code (react-dom).
  const stack =
    (event.type === 'error' ? event.error.stack : undefined) ??
    (context as { handlingStack?: string } | undefined)?.handlingStack
  const frameUrl = stack && getFirstFrameUrl(stack)
  if (!frameUrl) {
    return
  }

  for (const [baseUrl, build] of mfeBuildsByBaseUrl) {
    if (frameUrl.startsWith(baseUrl)) {
      event.service = build.service
      event.version = build.version
      return
    }
  }
}

const mfeVersionPlugin = (): ModuleFederationRuntimePlugin => {
  // Global so MFE federation instances also report the nested remotes they load themselves.
  registerGlobalPlugins([plugin])
  return plugin
}

// eslint-disable-next-line import/no-default-export
export default mfeVersionPlugin
